import React, { useEffect, useState } from "react";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Alert, Button, Empty, Skeleton, Table, Tag } from "antd";
import { ReloadOutlined, SendOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import InsideHeader from "../../components/InsideHeader";
import { fetchWhatsAppDashboard } from "../../features/whatsapp/whatsappSlice";
import { formatPercent, formatWhatsAppDate, statusChartColor, statusColor, truncate } from "./whatsappUtils";
import "./whatsapp.css";

const RANGE_OPTIONS = [7, 30, 90];

const summaryCards = (summary) => [
  { title: "Queued", value: summary?.totalQueued ?? 0, tone: "neutral" },
  { title: "Delivered", value: summary?.deliveredCount ?? 0, tone: "success" },
  { title: "Read", value: summary?.readCount ?? 0, tone: "accent" },
  { title: "Failed", value: summary?.failedCount ?? 0, tone: "danger" },
  { title: "Dead Letter", value: summary?.deadLetterCount ?? 0, tone: "warning" },
  { title: "Delivery Rate", value: formatPercent(summary?.deliveryRate ?? 0), tone: "success" },
];

const numberFormatter = (value) => Number(value || 0).toLocaleString();

const WhatsAppDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dashboardAnalytics, dashboardError, loadingDashboard } = useSelector((state) => state.whatsapp);
  const [days, setDays] = useState(30);

  const loadDashboard = (windowDays = days) => {
    dispatch(fetchWhatsAppDashboard({ days: windowDays }));
  };

  useEffect(() => {
    dispatch(fetchWhatsAppDashboard({ days: 30 }));
  }, [dispatch]);

  const summary = dashboardAnalytics?.summary;
  const statusBreakdown = dashboardAnalytics?.statusBreakdown ?? [];
  const dailySeries = dashboardAnalytics?.dailySeries ?? [];
  const topTemplates = dashboardAnalytics?.topTemplates ?? [];
  const topErrors = dashboardAnalytics?.topErrors ?? [];
  const recentMessages = dashboardAnalytics?.recentMessages ?? [];

  const recentColumns = [
    {
      title: "Recipient",
      dataIndex: "recipient",
      width: 150,
    },
    {
      title: "Type",
      dataIndex: "messageType",
      width: 120,
    },
    {
      title: "Template",
      dataIndex: "templateName",
      width: 180,
      render: (value) => value || "Text message",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (value) => <Tag color={statusColor(value)}>{value || "-"}</Tag>,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      width: 180,
      render: formatWhatsAppDate,
    },
    {
      title: "Delivered",
      dataIndex: "deliveredAt",
      width: 180,
      render: formatWhatsAppDate,
    },
    {
      title: "Read",
      dataIndex: "readAt",
      width: 180,
      render: formatWhatsAppDate,
    },
  ];

  return (
    <div className="w-full h-full whatsapp-page weiser-soft-scrollbar">
      <InsideHeader title="WhatsApp Dashboard" subtitle="Analytics for outbound WhatsApp performance" back={true} />

      <div className="whatsapp-dashboard-shell">
        <div className="whatsapp-dashboard-toolbar">
          <div className="whatsapp-range-toggle">
            {RANGE_OPTIONS.map((windowDays) => (
              <button
                key={windowDays}
                type="button"
                className={`whatsapp-range-button ${days === windowDays ? "is-active" : ""}`}
                onClick={() => {
                  setDays(windowDays);
                  loadDashboard(windowDays);
                }}
              >
                {windowDays} Days
              </button>
            ))}
          </div>

          <div className="whatsapp-dashboard-actions">
            <Button icon={<SendOutlined />} onClick={() => navigate("/whatsapp-send")}>
              Send
            </Button>
            <Button icon={<UnorderedListOutlined />} onClick={() => navigate("/whatsapp-queue")}>
              Queue
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => loadDashboard()} loading={loadingDashboard}>
              Refresh
            </Button>
          </div>
        </div>

        {loadingDashboard && !dashboardAnalytics ? (
          <Skeleton active />
        ) : dashboardError ? (
          <div className="whatsapp-dashboard-panel">
            <Alert
              type="error"
              showIcon
              message="Unable to load WhatsApp analytics"
              description={dashboardError}
            />
          </div>
        ) : !dashboardAnalytics ? (
          <div className="whatsapp-dashboard-panel">
            <Empty description="No WhatsApp analytics available yet" />
          </div>
        ) : (
          <>
            <div className="whatsapp-summary-grid">
              {summaryCards(summary).map((card) => (
                <div key={card.title} className={`whatsapp-summary-card tone-${card.tone}`}>
                  <span>{card.title}</span>
                  <strong>{typeof card.value === "number" ? numberFormatter(card.value) : card.value}</strong>
                </div>
              ))}
            </div>

            <div className="whatsapp-dashboard-grid">
              <section className="whatsapp-dashboard-panel chart-span">
                <div className="whatsapp-panel-title-row">
                  <div>
                    <h2>Message Trend</h2>
                    <p>Queued, sent, delivered, read, and failed activity over time.</p>
                  </div>
                </div>

                {dailySeries.length > 0 ? (
                  <div className="whatsapp-chart-frame">
                    <ResponsiveContainer>
                      <AreaChart data={dailySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#d7dde3" />
                        <XAxis dataKey="metricDate" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="queuedCount" stackId="1" stroke="#94a3b8" fill="#cbd5e1" name="Queued" />
                        <Area type="monotone" dataKey="sentCount" stackId="2" stroke="#06b6d4" fill="#67e8f9" name="Sent" />
                        <Area type="monotone" dataKey="deliveredCount" stackId="3" stroke="#16a34a" fill="#86efac" name="Delivered" />
                        <Area type="monotone" dataKey="readCount" stackId="4" stroke="#7c3aed" fill="#c4b5fd" name="Read" />
                        <Area type="monotone" dataKey="failedCount" stackId="5" stroke="#dc2626" fill="#fca5a5" name="Failed" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Empty description="No trend data in this period" />
                )}
              </section>

              <section className="whatsapp-dashboard-panel">
                <div className="whatsapp-panel-title-row">
                  <div>
                    <h2>Status Breakdown</h2>
                    <p>Current queue status distribution for this period.</p>
                  </div>
                </div>

                {statusBreakdown.length > 0 ? (
                  <div className="whatsapp-chart-frame compact">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={statusBreakdown} dataKey="count" nameKey="status" innerRadius={56} outerRadius={88} paddingAngle={2}>
                          {statusBreakdown.map((entry) => (
                            <Cell key={entry.status} fill={statusChartColor(entry.status)} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => numberFormatter(value)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Empty description="No status data available" />
                )}
              </section>

              <section className="whatsapp-dashboard-panel">
                <div className="whatsapp-panel-title-row">
                  <div>
                    <h2>Top Templates</h2>
                    <p>Highest-volume templates in the selected window.</p>
                  </div>
                </div>

                {topTemplates.length > 0 ? (
                  <div className="whatsapp-chart-frame compact">
                    <ResponsiveContainer>
                      <BarChart data={topTemplates} layout="vertical" margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#d7dde3" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="templateName" width={110} tickFormatter={(value) => truncate(value, 16)} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="totalCount" fill="#15803d" name="Queued" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="readCount" fill="#7c3aed" name="Read" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <Empty description="No template data available" />
                )}
              </section>

              <section className="whatsapp-dashboard-panel">
                <div className="whatsapp-panel-title-row">
                  <div>
                    <h2>Top Errors</h2>
                    <p>Most common dead-letter reasons in the selected window.</p>
                  </div>
                </div>

                {topErrors.length > 0 ? (
                  <div className="whatsapp-error-list">
                    {topErrors.map((item) => (
                      <div key={`${item.errorCode}-${item.errorMessage}`} className="whatsapp-error-card">
                        <div className="whatsapp-error-count">{numberFormatter(item.count)}</div>
                        <div className="whatsapp-error-copy">
                          <strong>{item.errorCode}</strong>
                          <span>{truncate(item.errorMessage, 140)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Empty description="No dead-letter errors in this period" />
                )}
              </section>
            </div>

            <section className="whatsapp-dashboard-panel">
              <div className="whatsapp-panel-title-row">
                <div>
                  <h2>Recent Activity</h2>
                  <p>Latest outbound WhatsApp messages in the selected period.</p>
                </div>
              </div>

              <Table
                columns={recentColumns}
                dataSource={recentMessages}
                rowKey={(record) => record.id}
                pagination={false}
                scroll={{ x: 1100 }}
                locale={{ emptyText: <Empty description="No recent WhatsApp activity" /> }}
              />
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default WhatsAppDashboard;
